import type { SearchPageData } from '~/modules-rocco/search/states/search-page-data-atom';

const MAX_CACHE_ENTRIES = 20;
const SEARCH_CACHE_KEY_PREFIX = 'searchResultCache_';
const SEARCH_CACHE_URL_LRU_KEY = 'searchResultCacheLRU';
const CACHE_EXPIRATION_MS = 30 * 60 * 1000;

interface CachedSearchData {
    data: SearchPageData;
    timestamp: number;
}

/**
 * Retrieves the list of recently used cache keys (URLs) from sessionStorage.
 * @returns An array of URLs.
 */
function getLruUrlList(): string[] {
    const lruListString = sessionStorage.getItem(SEARCH_CACHE_URL_LRU_KEY);
    return lruListString ? JSON.parse(lruListString) : [];
}

/**
 * Updates the list of recently used cache keys (URLs) in sessionStorage.
 * @param url The URL to add or move to the most recently used position.
 * @param lruList The current list of LRU URLs.
 */
function updateLruUrlList(url: string, lruList: string[]): string[] {
    const existingIndex = lruList.indexOf(url);
    if (existingIndex > -1) {
        lruList.splice(existingIndex, 1); // Remove existing entry
    }
    lruList.unshift(url); // Add to the front (most recently used)

    if (lruList.length > MAX_CACHE_ENTRIES) {
        const oldestUrl = lruList.pop(); // Remove the least recently used from JS array
        if (oldestUrl) {
            try {
                sessionStorage.removeItem(
                    `${SEARCH_CACHE_KEY_PREFIX}${oldestUrl}`
                );
            } catch (e) {
                console.warn(
                    `Failed to remove oldest item data ${oldestUrl} from storage during LRU update:`,
                    e
                );
                // If removing data fails, the LRU list in memory has popped it, but data may remain.
                // Proceed with updating the LRU list key itself.
            }
        }
    }

    try {
        sessionStorage.setItem(
            SEARCH_CACHE_URL_LRU_KEY,
            JSON.stringify(lruList)
        );
    } catch (e) {
        console.error('Failed to update LRU URL list in sessionStorage:', e);
        // If this critical step fails, the LRU mechanism is compromised.
        // The lruList in memory is now out of sync with what might be in storage.
        // Subsequent calls to getLruUrlList() will fetch from storage,
        // so this modified lruList (in memory) might be lost if the error is transient.
        // Allow setCachedSearchResults to handle its own primary setItem error.
    }
    return lruList;
}

/**
 * Retrieves cached search results for a given URL.
 * @param url The URL (including search parameters) for which to retrieve cached results.
 * @returns The cached SearchPageData or null if not found or expired.
 */
export function getCachedSearchResults(url: string): SearchPageData | null {
    const lruList = getLruUrlList();
    if (!lruList.includes(url)) {
        return null; // Not in LRU list, so effectively not cached or already evicted
    }

    const cachedDataString = sessionStorage.getItem(
        `${SEARCH_CACHE_KEY_PREFIX}${url}`
    );
    if (!cachedDataString) {
        // Data might have been removed manually or due to storage limits, clean up LRU
        const newLruList = lruList.filter((u) => u !== url); // Create list without the problematic URL
        sessionStorage.setItem(
            SEARCH_CACHE_URL_LRU_KEY,
            JSON.stringify(newLruList)
        ); // Save the cleaned list
        return null;
    }

    try {
        const cachedItem = JSON.parse(cachedDataString) as CachedSearchData;
        if (Date.now() - cachedItem.timestamp > CACHE_EXPIRATION_MS) {
            sessionStorage.removeItem(`${SEARCH_CACHE_KEY_PREFIX}${url}`);
            const newLruList = lruList.filter((u) => u !== url);
            sessionStorage.setItem(
                SEARCH_CACHE_URL_LRU_KEY,
                JSON.stringify(newLruList)
            );
            return null; // Expired
        }
        return cachedItem.data;
    } catch (error) {
        console.error('Error parsing cached search results:', error);
        sessionStorage.removeItem(`${SEARCH_CACHE_KEY_PREFIX}${url}`); // Remove corrupted data
        const newLruList = lruList.filter((u) => u !== url); // Create list without the problematic URL
        sessionStorage.setItem(
            SEARCH_CACHE_URL_LRU_KEY,
            JSON.stringify(newLruList)
        ); // Save the cleaned list
        return null;
    }
}

/**
 * Stores search results in sessionStorage for a given URL.
 * Manages the LRU list of cached URLs.
 * @param url The URL (including search parameters) for which to store results.
 * @param data The SearchPageData to cache.
 */
export function setCachedSearchResults(
    url: string,
    data: SearchPageData
): void {
    try {
        // Step 1: Update LRU list. This may remove the oldest item if cache is at MAX_CACHE_ENTRIES.
        // It internally handles errors for its own sessionStorage operations.
        updateLruUrlList(url, getLruUrlList());

        // Step 2: Prepare and try to store the actual search data
        const itemToCache: CachedSearchData = {
            data,
            timestamp: Date.now(),
        };
        if (!data.results && !data.searchResultGroups) return;
        const dataString = JSON.stringify(itemToCache);
        sessionStorage.setItem(`${SEARCH_CACHE_KEY_PREFIX}${url}`, dataString);
    } catch (error) {
        const isQuotaError =
            error instanceof DOMException &&
            (error.name === 'QuotaExceededError' ||
                error.name === 'NS_ERROR_DOM_QUOTA_REACHED');

        console.error(
            `Error storing search results for ${url} in cache:`,
            error
        );

        // Attempt to clean up LRU list if 'url' was added but its data failed to save.
        try {
            let currentLruList = getLruUrlList();
            const indexOfFailedUrl = currentLruList.indexOf(url);

            if (indexOfFailedUrl > -1) {
                currentLruList.splice(indexOfFailedUrl, 1); // Remove 'url' as its data isn't cached
                // Attempt to save the corrected LRU list
                sessionStorage.setItem(
                    SEARCH_CACHE_URL_LRU_KEY,
                    JSON.stringify(currentLruList)
                );
                console.info(
                    `Removed ${url} from LRU list due to caching failure.`
                );
            }

            // If the initial error was a QuotaExceededError for the data item,
            // try to free up more space by removing the current oldest item.
            if (isQuotaError) {
                console.warn(
                    `Original error for ${url} was QuotaExceededError. Attempting to clear oldest cache entry.`
                );
                // Refresh currentLruList as it might have changed if indexOfFailedUrl was found and spliced.
                // Or, if url was not in the list initially (e.g. LRU update itself failed partially),
                // get the current state before proceeding.
                currentLruList = getLruUrlList();

                if (currentLruList.length > 0) {
                    const oldestUrlToClear = currentLruList.pop(); // Get and remove from JS array
                    if (oldestUrlToClear) {
                        sessionStorage.removeItem(
                            `${SEARCH_CACHE_KEY_PREFIX}${oldestUrlToClear}`
                        );
                        // Update LRU list in storage again after this data removal
                        sessionStorage.setItem(
                            SEARCH_CACHE_URL_LRU_KEY,
                            JSON.stringify(currentLruList)
                        );
                        console.log(
                            `Removed oldest cached item ${oldestUrlToClear} and its data to free space, after ${url} failed with quota error.`
                        );
                    }
                } else {
                    console.warn(
                        `LRU list already empty or became empty. Cannot clear more space for ${url}.`
                    );
                }
            }
        } catch (cleanupError) {
            console.error(
                `Error during cache cleanup operations for ${url} after initial storage failure:`,
                cleanupError
            );
        }
    }
}
